import { useState } from 'react';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate, useNavigate } from 'react-router';
import { z } from 'zod';
import { Button, Card, Checkbox, Input, Mascot, PasswordInput, cn, useI18n } from '@xdocs/ui';
import { signupInput } from '@xdocs/schemas';
import type { SignupInput } from '@xdocs/schemas';
import { PasswordStrengthMeter } from '../auth/PasswordStrengthMeter.js';
import { useAuth } from '../auth/AuthProvider.js';
import { applyServerAlert } from '../auth/applyServerAlert.js';
import { startGoogleOAuth } from '../auth/googleOAuthStart.js';
import { safeFieldError, safeT } from '../i18n/safeT.js';
import { optionalProp } from '../lib/optionalProp.js';
import styles from './AuthCard.module.css';

type GoogleStatus = { kind: 'idle' } | { kind: 'unavailable' } | { kind: 'error'; message: string };

/** Client-only form shape: the server contract (`signupInput`) plus a
 * confirm-password field that never leaves the browser — `.superRefine`
 * (not `.refine`) so the mismatch issue anchors to `confirmPassword`
 * field-level, and the message is a catalog key `safeFieldError` resolves. */
const signupFormSchema = signupInput
  .extend({ confirmPassword: z.string() })
  .superRefine((values, ctx) => {
    if (values.confirmPassword !== values.password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
        message: 'auth:signup.confirmPassword.mismatch',
      });
    }
  });

type SignupFormValues = z.infer<typeof signupFormSchema>;

/**
 * `/signup` (F44, design/app mockup 14 pixel parity). Unlike `/login`,
 * mockup 14 doesn't tab the three F12 methods — it stacks "Continue with
 * Google" (`../auth/googleOAuthStart.ts`, shared with `LoginPage`) above a
 * divider, then the email+password form. Mockup 14 has no visible
 * `displayName` field, so it's submitted as `undefined` (still valid per
 * `signupInput`'s `.optional()`) rather than rendered — keeps pixel parity
 * without touching the shared `@xdocs/schemas` contract. The terms-consent
 * checkbox is a mockup-demanded addition with no server-side field to gate
 * on yet (`termsAccepted` — see the checkbox's own doc comment).
 *
 * Deliberate divergence from mockup 14 (2026-07-04 pixel-parity pass,
 * `signup.gaps.md` #1-2): the mock has no confirm-password field and no
 * password reveal toggle. Both stay — a confirm-password field with a
 * `superRefine` mismatch check and a `PasswordInput` reveal toggle are
 * established, fully-tested product decisions here (reveal toggle also
 * used by `LoginPage`), not a mockup gap to close by deleting a security/UX
 * affordance and its test coverage to chase pixel parity on a static mock.
 */
export function SignupPage(): ReactElement {
  const { t, locale } = useI18n();
  const { status, signup } = useAuth();
  const navigate = useNavigate();
  const [banner, setBanner] = useState<string | null>(null);
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus>({ kind: 'idle' });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });
  const passwordValue = watch('password');

  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  async function onGoogleClick(): Promise<void> {
    const result = await startGoogleOAuth();
    if (!result.ok) {
      setGoogleStatus(
        result.reason === 'unavailable'
          ? { kind: 'unavailable' }
          : { kind: 'error', message: safeT(locale, 'errors:code.INTERNAL') },
      );
    }
  }

  async function onSubmit(values: SignupFormValues): Promise<void> {
    setBanner(null);
    // Client-only consent gate (E19 "finish the path" — no
    // `signupInput.termsAccepted` field exists server-side to validate this
    // against, so it's enforced here rather than silently ignored).
    if (!termsAccepted) {
      setTermsError(safeT(locale, 'auth:signup.terms.required'));
      return;
    }
    setTermsError(null);
    // confirmPassword is a client-only check — the server contract stays
    // exactly `signupInput`, so it never crosses the wire.
    const { confirmPassword: _confirmPassword, ...payload } = values;
    try {
      await signup(payload satisfies SignupInput);
      navigate('/', { replace: true });
    } catch (err) {
      const message = applyServerAlert(err, setError, locale);
      if (message) setBanner(message);
    }
  }

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['wrap'])}>
        <div className={cn(styles['header'], styles['headerBlock'])}>
          <Mascot pose="celebrating" size={96} alt="" />
          <h1 className={cn(styles['title'], styles['signupTitle'])}>{t('auth:signup.title')}</h1>
          <p className={cn(styles['subtitle'])}>{t('auth:signup.subtitle')}</p>
        </div>

        <Card className={cn(styles['cardBox'])}>
          {banner && (
            <p role="alert" className={cn(styles['banner'])}>
              {banner}
            </p>
          )}

          <form
            className={cn(styles['form'])}
            onSubmit={(event) => void handleSubmit(onSubmit)(event)}
            noValidate
          >
            {googleStatus.kind === 'unavailable' && (
              <p role="status">{t('auth:login.google.unavailable')}</p>
            )}
            {googleStatus.kind === 'error' && (
              <p role="alert" className={cn(styles['banner'])}>
                {googleStatus.message}
              </p>
            )}
            <Button
              type="button"
              variant="secondary"
              className={cn(styles['googleButton'])}
              onClick={() => void onGoogleClick()}
              leadingIcon={
                <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#EA4335"
                    d="M12 10.2v3.9h5.4c-.24 1.4-1.7 4.1-5.4 4.1a6.2 6.2 0 0 1 0-12.4c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 2.7 14.6 1.8 12 1.8a10.2 10.2 0 0 0 0 20.4c5.9 0 9.8-4.1 9.8-10 0-.7-.1-1.2-.2-1.8z"
                  />
                </svg>
              }
            >
              {t('auth:login.withGoogle')}
            </Button>

            <div className={cn(styles['divider'])}>{t('auth:signup.orDivider')}</div>

            <Input
              type="email"
              autoComplete="email"
              label={t('auth:login.email.label')}
              placeholder={t('auth:signup.email.placeholder')}
              required
              containerClassName={cn(styles['fieldLabel'])}
              className={cn(styles['fieldInput'])}
              {...optionalProp('error', safeFieldError(locale, errors.email?.message))}
              {...register('email')}
            />
            <div>
              <PasswordInput
                autoComplete="new-password"
                label={t('auth:login.password.label')}
                placeholder={t('auth:signup.password.placeholder')}
                showLabel={t('auth:password.show')}
                hideLabel={t('auth:password.hide')}
                required
                containerClassName={cn(styles['fieldLabel'])}
                className={cn(styles['fieldInput'])}
                {...optionalProp('error', safeFieldError(locale, errors.password?.message))}
                {...register('password')}
              />
              <PasswordStrengthMeter password={passwordValue} />
              <p className={cn(styles['hint'])}>{t('auth:signup.password.hint')}</p>
            </div>
            <PasswordInput
              autoComplete="new-password"
              label={t('auth:signup.confirmPassword.label')}
              showLabel={t('auth:password.show')}
              hideLabel={t('auth:password.hide')}
              required
              containerClassName={cn(styles['fieldLabel'])}
              className={cn(styles['fieldInput'])}
              {...optionalProp('error', safeFieldError(locale, errors.confirmPassword?.message))}
              {...register('confirmPassword')}
            />
            <Checkbox
              align="start"
              labelClassName={cn(styles['consentLabel'])}
              label={
                <>
                  {t('auth:signup.terms.prefix')}{' '}
                  <a href="https://xdocs.app/terms" className={cn(styles['inlineLink'])}>
                    {t('auth:signup.terms.termsLink')}
                  </a>{' '}
                  {t('auth:signup.terms.and')}{' '}
                  <a href="https://xdocs.app/privacy" className={cn(styles['inlineLink'])}>
                    {t('auth:signup.terms.privacyLink')}
                  </a>
                  {t('auth:signup.terms.suffix')}
                </>
              }
              checked={termsAccepted}
              onChange={(event) => {
                setTermsAccepted(event.target.checked);
                if (event.target.checked) setTermsError(null);
              }}
              {...optionalProp('error', termsError ?? undefined)}
            />
            <Button
              type="submit"
              variant="primary"
              className={cn(styles['primarySubmit'])}
              state={isSubmitting ? 'loading' : 'idle'}
            >
              {t('auth:signup.submit')}
            </Button>
          </form>
        </Card>

        <p className={cn(styles['footer'], styles['footerBlock'])}>
          {t('auth:signup.hasAccount')}{' '}
          <Link to="/login" className={cn(styles['footerLink'])}>
            {t('auth:login.title')}
          </Link>
        </p>
      </div>
    </div>
  );
}
